import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface MetabaseEmbedConfig {
  theme: {
    preset: 'light' | 'dark';
  };
  isGuest: boolean;
  instanceUrl: string;
}

interface MetabaseTokenResponse {
  token: string;
}

declare global {
  interface Window {
    metabaseConfig?: MetabaseEmbedConfig;
  }
}

@Injectable({
  providedIn: 'root',
})
export class MetabaseService {
  private readonly scriptId = 'metabase-embed-script';
  private readonly scriptUrl = 'https://metabase.finbondsa.solutions/app/embed.js';
  private readonly defaultConfig: MetabaseEmbedConfig = {
    theme: { preset: 'light' },
    isGuest: true,
    instanceUrl: 'https://metabase.finbondsa.solutions',
  };

  private metabase_apiURL = environment.metabaseApiUrl;

  private scriptLoadPromise: Promise<boolean> | null = null;

  constructor(private http: HttpClient) {}

  initializeEmbed(config?: Partial<MetabaseEmbedConfig>): Promise<boolean> {
    this.applyConfig(config);
    return this.ensureEmbedScript();
  }

  getDashboardToken(dashboardId: number): Observable<string> {
    const params = new HttpParams().set('dashboard', String(dashboardId));
    return this.http
      .get<MetabaseTokenResponse>(`${this.metabase_apiURL}/token`, { params })
      .pipe(map((response) => response.token));
  }

  getQuestionToken(questionId: number): Observable<string> {
    const params = new HttpParams().set('question', String(questionId));
    return this.http
      .get<MetabaseTokenResponse>(`${this.metabase_apiURL}/token`, { params })
      .pipe(map((response) => response.token));
  }

  private applyConfig(config?: Partial<MetabaseEmbedConfig>): void {
    window.metabaseConfig = {
      ...this.defaultConfig,
      ...config,
      theme: {
        ...this.defaultConfig.theme,
        ...(config?.theme ?? {}),
      },
    };
  }

  private ensureEmbedScript(): Promise<boolean> {
    if (customElements.get('metabase-question') || customElements.get('metabase-dashboard')) {
      return Promise.resolve(true);
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    const existingScript = document.getElementById(this.scriptId) as HTMLScriptElement | null;
    if (existingScript) {
      const loaded = existingScript.getAttribute('data-loaded') === 'true';
      if (loaded) {
        return Promise.resolve(true);
      }

      this.scriptLoadPromise = new Promise((resolve) => {
        existingScript.addEventListener('load', () => resolve(true), { once: true });
        existingScript.addEventListener(
          'error',
          () => {
            this.scriptLoadPromise = null;
            existingScript.remove();
            resolve(false);
          },
          { once: true },
        );
      });
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.id = this.scriptId;
      script.src = this.scriptUrl;
      script.defer = true;
      script.setAttribute('data-loaded', 'false');
      script.addEventListener(
        'load',
        () => {
          script.setAttribute('data-loaded', 'true');
          resolve(true);
        },
        { once: true },
      );
      script.addEventListener(
        'error',
        () => {
          this.scriptLoadPromise = null;
          script.remove();
          resolve(false);
        },
        { once: true },
      );
      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }
}
